import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  GetResourcesAction,
  CreateResourceAction,
  UpdateResourceAction,
  DeleteResourceAction,
  GetOrganizationMembersAction,
  GetSubscriptionAction,
  ResendResourceInviteAction,
  RevokeResourceInviteAction,
  getUserInfoFromToken,
} from "../../../store/action";
import { UserRole } from "../../../constants/roles";
import {
  formatMoney,
  PlanLimitCode,
  isPlanLimitError,
} from "../../../constants/subscription";
import {
  pageTitleClass,
  pageSubtitleClass,
  cardClass,
  cardHeaderClass,
  cardTitleClass,
  cardBodyClass,
  btnPrimaryClass,
  btnGhostClass,
  formLabelClass,
  formControlClass,
  requiredMarkClass,
  formGridClass,
  alertErrorClass,
  listGridClass,
  listItemClass,
  listItemTitleClass,
  itemBadgeClass,
  badgeClass,
  btnIconEditClass,
  btnIconDeleteClass,
  btnIconConfirmClass,
  modalOverlayClass,
  modalPanelClass,
  modalHeaderClass,
  modalBodyClass,
  modalFooterClass,
  modalCloseClass,
  spinnerClass,
} from "../dashboardStyles";
import WeeklySchedule from "./wizard/components/WeeklySchedule";
import TimeOffEditor from "./wizard/components/TimeOffEditor";
import { notify, notifySaveError } from "../../../utils/notify";

const Resources = () => {
  const { t } = useTranslation();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  // People in the org who can be assigned to staff a resource.
  const [members, setMembers] = useState([]);
  const [formData, setFormData] = useState({
    type: "Person",
    name: "",
    email: "",
    userId: "",
    scheduleMode: "weekly",
    bookingAdvanceDays: 30,
    workingDays: {
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: false,
      sunday: false,
    },
    workingHours: {
      start: "09:00",
      end: "17:00",
    },
    useAdvancedSchedule: false,
    advancedSchedule: {
      monday: { enabled: true, start: "09:00", end: "17:00" },
      tuesday: { enabled: true, start: "09:00", end: "17:00" },
      wednesday: { enabled: true, start: "09:00", end: "17:00" },
      thursday: { enabled: true, start: "09:00", end: "17:00" },
      friday: { enabled: true, start: "09:00", end: "17:00" },
      saturday: { enabled: false, start: "09:00", end: "17:00" },
      sunday: { enabled: false, start: "09:00", end: "17:00" },
    },
    monthlySchedule: {},
  });
  const [saving, setSaving] = useState(false);
  // Plan usage, so we can show "X / N team members" and stop at the cap.
  const [usage, setUsage] = useState(null);
  // Employees are confined to the resource they staff (enforced by the API);
  // the picker below is disabled for them so the UI doesn't offer a
  // reassignment the backend will just refuse.
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    setUserInfo(getUserInfoFromToken());
  }, []);

  const isAdmin =
    userInfo &&
    (userInfo.role === UserRole.SYSTEM_OWNER ||
      userInfo.role === UserRole.ADMIN ||
      userInfo.role === UserRole.COMPANY_ADMIN);

  useEffect(() => {
    fetchResources();
  }, []);

  const refreshUsage = () => {
    GetSubscriptionAction()
      .then(setUsage)
      .catch(() => setUsage(null)); // no org yet / unavailable — hide the meter
  };

  useEffect(() => {
    refreshUsage();
  }, []);

  useEffect(() => {
    GetOrganizationMembersAction()
      .then(setMembers)
      .catch(() => {
        // Without the member list the picker just shows "unassigned"; the rest
        // of the page still works, so this stays quiet.
        setMembers([]);
      });
  }, []);

  const fetchResources = async () => {
    try {
      setLoading(true);
      const data = await GetResourcesAction();
      setResources(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching resources:", err);
      setError(t("Dashboard.Resources.loadError"));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (resource = null) => {
    if (resource) {
      setEditingResource(resource);
      setFormData({
        type: resource.type || "Person",
        name: resource.name || "",
        email: resource.email || "",
        userId: resource.userId ? String(resource.userId) : "",
        scheduleMode: resource.scheduleMode || "weekly",
        bookingAdvanceDays: resource.bookingAdvanceDays || 30,
        workingDays: resource.workingDays || {
          monday: true,
          tuesday: true,
          wednesday: true,
          thursday: true,
          friday: true,
          saturday: false,
          sunday: false,
        },
        workingHours: resource.workingHours || {
          start: "09:00",
          end: "17:00",
        },
        useAdvancedSchedule: resource.useAdvancedSchedule || false,
        advancedSchedule: resource.advancedSchedule || {
          monday: { enabled: true, start: "09:00", end: "17:00" },
          tuesday: { enabled: true, start: "09:00", end: "17:00" },
          wednesday: { enabled: true, start: "09:00", end: "17:00" },
          thursday: { enabled: true, start: "09:00", end: "17:00" },
          friday: { enabled: true, start: "09:00", end: "17:00" },
          saturday: { enabled: false, start: "09:00", end: "17:00" },
          sunday: { enabled: false, start: "09:00", end: "17:00" },
        },
        monthlySchedule: resource.monthlySchedule || {},
      });
    } else {
      setEditingResource(null);
      setFormData({
        type: "Person",
        name: "",
        email: "",
        userId: "",
        scheduleMode: "weekly",
        bookingAdvanceDays: 30,
        workingDays: {
          monday: true,
          tuesday: true,
          wednesday: true,
          thursday: true,
          friday: true,
          saturday: false,
          sunday: false,
        },
        workingHours: {
          start: "09:00",
          end: "17:00",
        },
        useAdvancedSchedule: false,
        advancedSchedule: {
          monday: { enabled: true, start: "09:00", end: "17:00" },
          tuesday: { enabled: true, start: "09:00", end: "17:00" },
          wednesday: { enabled: true, start: "09:00", end: "17:00" },
          thursday: { enabled: true, start: "09:00", end: "17:00" },
          friday: { enabled: true, start: "09:00", end: "17:00" },
          saturday: { enabled: false, start: "09:00", end: "17:00" },
          sunday: { enabled: false, start: "09:00", end: "17:00" },
        },
        monthlySchedule: {},
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingResource(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleScheduleUpdate = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const resourceData = {
        type: formData.type,
        name: formData.name,
        email: formData.email || null,
        // Only Person resources are staffed by someone; a room has no owner.
        userId:
          formData.type === "Person" && formData.userId
            ? parseInt(formData.userId, 10)
            : null,
        // The weekly hours always apply; the month calendar is now leave and
        // breaks on top of them (see TimeOffEditor), not a second mode.
        scheduleMode: "weekly",
        bookingAdvanceDays: parseInt(formData.bookingAdvanceDays),
        workingDays: formData.workingDays,
        workingHours: formData.workingHours,
        useAdvancedSchedule: formData.useAdvancedSchedule,
        advancedSchedule: formData.useAdvancedSchedule
          ? formData.advancedSchedule
          : null,
        // Always sent. In "weekly" mode it used to go as null, which erased
        // whatever days off the month view had recorded.
        monthlySchedule: formData.monthlySchedule,
      };

      if (editingResource) {
        await UpdateResourceAction(editingResource.id, resourceData);
      } else {
        await CreateResourceAction(resourceData);
      }

      await fetchResources();
      refreshUsage();
      handleCloseModal();
      notify.success(
        t(editingResource ? "Dashboard.Resources.updated" : "Dashboard.Resources.created"),
      );
    } catch (err) {
      // The snackbar rather than the page alert: the modal is still open over
      // the page, and the alert used to sit behind it.
      console.error("Error saving resource:", err);
      // A 402 is the plan refusing another specialist — a warning, not a fault
      // — said with the hint that points at the upgrade. Other errors keep
      // the server's message, falling back to the generic save error.
      if (isPlanLimitError(err, PlanLimitCode.STAFF)) {
        notify.warning(t("Dashboard.Plan.staffCapHint"));
      } else {
        notifySaveError(err, t("Dashboard.Resources.saveError"));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("Dashboard.Resources.deleteConfirm"))) {
      return;
    }

    try {
      await DeleteResourceAction(id);
      notify.success(t("Dashboard.Resources.deleted"));
      await fetchResources();
      refreshUsage();
    } catch (err) {
      console.error("Error deleting resource:", err);
      notifySaveError(err, t("Dashboard.Resources.deleteError"));
    }
  };

  const handleResendInvite = async (resource) => {
    try {
      await ResendResourceInviteAction(resource.id);
      notify.success(
        t("Dashboard.Resources.inviteResent", { name: resource.name }),
      );
      await fetchResources();
    } catch (err) {
      console.error("Error resending invite:", err);
      notifySaveError(err, t("Dashboard.Resources.inviteResendError"));
    }
  };

  const handleRevokeInvite = async (resource) => {
    if (
      !window.confirm(
        t("Dashboard.Resources.inviteRevokeConfirm", { name: resource.name }),
      )
    ) {
      return;
    }

    try {
      await RevokeResourceInviteAction(resource.id);
      notify.success(t("Dashboard.Resources.inviteRevoked"));
      await fetchResources();
    } catch (err) {
      console.error("Error revoking invite:", err);
      notifySaveError(err, t("Dashboard.Resources.inviteRevokeError"));
    }
  };

  // Colours mirror the status-badge convention on the Bookings page: brand
  // for "done", sky for "in flight", amber for "waiting", red/ink for dead.
  const getInviteStatusBadgeClass = (status) => {
    switch (status) {
      case "Accepted":
        return "bg-brand-50 text-brand-700";
      case "Sent":
        return "bg-sky-100 text-sky-800";
      case "Pending":
        return "bg-amber-100 text-amber-800";
      case "Expired":
        return "bg-red-50 text-red-700";
      case "Revoked":
      default:
        return "bg-ink-100 text-ink-600";
    }
  };

  const getWorkingDaysDisplay = (workingDays) => {
    const days = [];
    if (workingDays.monday) days.push(t("Dashboard.Resources.daysShort.mon"));
    if (workingDays.tuesday) days.push(t("Dashboard.Resources.daysShort.tue"));
    if (workingDays.wednesday)
      days.push(t("Dashboard.Resources.daysShort.wed"));
    if (workingDays.thursday) days.push(t("Dashboard.Resources.daysShort.thu"));
    if (workingDays.friday) days.push(t("Dashboard.Resources.daysShort.fri"));
    if (workingDays.saturday) days.push(t("Dashboard.Resources.daysShort.sat"));
    if (workingDays.sunday) days.push(t("Dashboard.Resources.daysShort.sun"));
    return days.join(", ");
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div
          role="status"
          aria-label={t("Dashboard.Resources.loading")}
          className={spinnerClass}
        />
      </div>
    );
  }

  // At the team-member cap for the current plan — the "Add" button is blocked
  // and an upgrade hint shows. The backend is still the real gate (402).
  const atStaffCap =
    usage && usage.staffLimit != null && usage.staffUsed >= usage.staffLimit;

  // Below the cap, but the plan's included seats are already used up, so the
  // next person added is a chargeable one. Said before the click rather than
  // after it: the owner is about to change what the business pays, and finding
  // that out from an invoice is how a plan limit becomes a complaint.
  const nextSeatCosts =
    !atStaffCap &&
    usage &&
    usage.extraSeatPrice != null &&
    usage.staffUsed >= usage.includedSeats;

  return (
    <div>
      <div className="mb-6">
        <h1 className={pageTitleClass}>{t("Dashboard.Resources.title")}</h1>
        <p className={pageSubtitleClass}>
          {t("Dashboard.Resources.subtitle")}
        </p>
      </div>

      {error && (
        <div className={alertErrorClass} role="alert">
          <i className="bi bi-exclamation-circle mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className={cardClass}>
        <div className={cardHeaderClass}>
          <h3 className={cardTitleClass}>
            {t("Dashboard.Resources.allResources")}
          </h3>
          <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
            {usage && usage.staffLimit != null && (
              <span
                className={`text-xs font-medium ${
                  atStaffCap ? "text-amber-700" : "text-ink-500"
                }`}
              >
                {t("Dashboard.Plan.teamUsage", {
                  used: usage.staffUsed,
                  limit: usage.staffLimit,
                })}
              </span>
            )}
            <button
              className={btnPrimaryClass}
              onClick={() => handleOpenModal()}
              disabled={atStaffCap}
              title={
                atStaffCap
                  ? t("Dashboard.Plan.staffCapReached", {
                      count: usage.staffLimit,
                    })
                  : undefined
              }
            >
              <i className="bi bi-plus-circle" />
              {t("Dashboard.Resources.addResource")}
            </button>
          </div>
        </div>
        {atStaffCap && (
          <div className="border-b border-amber-200 bg-amber-50 px-5 py-2.5 text-xs text-amber-800">
            <i className="bi bi-info-circle mr-1" aria-hidden="true" />
            {t("Dashboard.Plan.staffCapHint")}{" "}
            <a
              href="/dashboard/settings"
              className="font-semibold underline hover:text-amber-900"
            >
              {t("Dashboard.Plan.upgradeLink")}
            </a>
          </div>
        )}
        {/* Same warning, same weight as the wizard's — it is the one sentence
            that tells an owner the next hire is chargeable, and a version of it
            that reads as a footnote here would undo the point of saying it
            loudly there. */}
        {nextSeatCosts && (
          <div
            className="border-b-2 border-red-300 bg-red-50 px-5 py-3 text-fluid-sm font-semibold text-red-700"
            role="alert"
            data-testid="seat-cost-warning"
          >
            <i
              className="bi bi-exclamation-triangle-fill mr-1.5"
              aria-hidden="true"
            />
            {t("Dashboard.Plan.seatCostHint", {
              price: formatMoney(usage.extraSeatPrice, usage.currency),
            })}
          </div>
        )}
        <div className={cardBodyClass}>
          {resources.length === 0 ? (
            <p className="py-8 text-center text-fluid-sm text-ink-500">
              {t("Dashboard.Resources.emptyState")}
            </p>
          ) : (
            <div className={listGridClass}>
              {resources.map((resource) => (
                <div key={resource.id} className={listItemClass}>
                  <div className="min-w-0 flex-1">
                    <h4 className={listItemTitleClass}>{resource.name}</h4>
                    {resource.email && (
                      <p className="mt-1 flex flex-wrap items-center gap-2 text-fluid-sm text-ink-500">
                        <span className="truncate">📧 {resource.email}</span>
                        {resource.inviteStatus && (
                          <span
                            className={`${badgeClass} ${getInviteStatusBadgeClass(resource.inviteStatus)}`}
                          >
                            {t(
                              `Dashboard.Resources.inviteStatus.${resource.inviteStatus.toLowerCase()}`,
                            )}
                          </span>
                        )}
                      </p>
                    )}
                    <div className="mt-2.5 flex flex-wrap gap-1.5">
                      <span className={itemBadgeClass}>{resource.type}</span>
                      {resource.workingDays && (
                        <span className={itemBadgeClass}>
                          {getWorkingDaysDisplay(resource.workingDays)}
                        </span>
                      )}
                      {resource.workingHours && (
                        <span className={itemBadgeClass}>
                          ⏰ {resource.workingHours.start} -{" "}
                          {resource.workingHours.end}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-1.5">
                    {resource.inviteStatus && resource.inviteStatus !== "Accepted" && (
                      <button
                        className={btnIconConfirmClass}
                        onClick={() => handleResendInvite(resource)}
                        title={t("Dashboard.Resources.inviteResend")}
                        aria-label={t("Dashboard.Resources.inviteResendAriaLabel", {
                          name: resource.name,
                        })}
                      >
                        <i className="bi bi-send" />
                      </button>
                    )}
                    {(resource.inviteStatus === "Pending" ||
                      resource.inviteStatus === "Sent") && (
                      <button
                        className={btnIconDeleteClass}
                        onClick={() => handleRevokeInvite(resource)}
                        title={t("Dashboard.Resources.inviteRevoke")}
                        aria-label={t("Dashboard.Resources.inviteRevokeAriaLabel", {
                          name: resource.name,
                        })}
                      >
                        <i className="bi bi-x-circle" />
                      </button>
                    )}
                    <button
                      className={btnIconEditClass}
                      onClick={() => handleOpenModal(resource)}
                      aria-label={t("Dashboard.Resources.editAriaLabel", {
                        name: resource.name,
                      })}
                    >
                      <i className="bi bi-pencil" />
                    </button>
                    <button
                      className={btnIconDeleteClass}
                      onClick={() => handleDelete(resource.id)}
                      aria-label={t("Dashboard.Resources.deleteAriaLabel", {
                        name: resource.name,
                      })}
                    >
                      <i className="bi bi-trash" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Resource Modal */}
      {showModal && (
        <div
          className={modalOverlayClass}
          onClick={handleCloseModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="resource-modal-title"
        >
          <div className={modalPanelClass} onClick={(e) => e.stopPropagation()}>
            <div className={modalHeaderClass}>
              <h3 id="resource-modal-title" className={cardTitleClass}>
                {editingResource
                  ? t("Dashboard.Resources.modal.editTitle")
                  : t("Dashboard.Resources.modal.addTitle")}
              </h3>
              <button
                className={modalCloseClass}
                onClick={handleCloseModal}
                aria-label={t("Dashboard.Resources.modal.close")}
              >
                <i className="bi bi-x text-xl" />
              </button>
            </div>
            <form
              onSubmit={handleSubmit}
              className="flex min-h-0 flex-1 flex-col"
            >
              <div className={`${modalBodyClass} flex flex-col gap-4`}>
                <div className={formGridClass}>
                  <div>
                    <label htmlFor="type" className={formLabelClass}>
                      {t("Dashboard.Resources.form.type")}{" "}
                      <span className={requiredMarkClass}>*</span>
                    </label>
                    <select
                      id="type"
                      name="type"
                      value={formData.type}
                      onChange={handleFormChange}
                      required
                      className={formControlClass}
                    >
                      <option value="Person">
                        {t("Dashboard.Resources.form.typePerson")}
                      </option>
                      <option value="Room">
                        {t("Dashboard.Resources.form.typeRoom")}
                      </option>
                      <option value="Equipment">
                        {t("Dashboard.Resources.form.typeEquipment")}
                      </option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="name" className={formLabelClass}>
                      {t("Dashboard.Resources.form.name")}{" "}
                      <span className={requiredMarkClass}>*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      required
                      className={formControlClass}
                      placeholder={t("Dashboard.Resources.form.namePlaceholder")}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className={formLabelClass}>
                    {t("Dashboard.Resources.form.email")}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    className={formControlClass}
                    placeholder={t("Dashboard.Resources.form.emailPlaceholder")}
                  />
                </div>

                {formData.type === "Person" && (
                  <div>
                    <label htmlFor="userId" className={formLabelClass}>
                      {t("Dashboard.Resources.form.assignedUser")}
                    </label>
                    <select
                      id="userId"
                      name="userId"
                      value={formData.userId}
                      onChange={handleFormChange}
                      disabled={!isAdmin}
                      className={formControlClass}
                    >
                      <option value="">
                        {t("Dashboard.Resources.form.assignedUserNone")}
                      </option>
                      {members.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.firstName} {m.lastName} ({m.email})
                        </option>
                      ))}
                    </select>
                    <p className="mt-1.5 text-xs text-ink-500">
                      {isAdmin
                        ? t("Dashboard.Resources.form.assignedUserHint")
                        : t("Dashboard.Resources.form.assignedUserLocked")}
                    </p>
                  </div>
                )}

                <div>
                  <label htmlFor="bookingAdvanceDays" className={formLabelClass}>
                    {t("Dashboard.Resources.form.bookingAdvanceDays")}
                  </label>
                  <input
                    type="number"
                    id="bookingAdvanceDays"
                    name="bookingAdvanceDays"
                    value={formData.bookingAdvanceDays}
                    onChange={handleFormChange}
                    min="1"
                    max="365"
                    className={formControlClass}
                  />
                  <small className="mt-1 block text-xs text-ink-500">
                    {t("Dashboard.Resources.form.bookingAdvanceDaysHint")}
                  </small>
                </div>

                <div className="rounded-lg border border-ink-200 p-4">
                  <h4 className="m-0 mb-4 text-sm font-semibold text-ink-900">
                    {t("Dashboard.Resources.form.workingSchedule")}
                  </h4>
                  <WeeklySchedule
                    resource={formData}
                    onUpdate={handleScheduleUpdate}
                  />
                </div>

                {/* Leave, days off and breaks, taken out of the hours above.
                    An employee reaches this page as "Availability" for the
                    resource they staff, so they set their own. */}
                <div className="rounded-lg border border-ink-200 p-4">
                  <h4 className="m-0 mb-3 flex items-center gap-2 text-sm font-semibold text-ink-900">
                    <i className="bi bi-airplane text-brand-600" aria-hidden="true" />
                    {t("Dashboard.TimeOff.title")}
                  </h4>
                  <TimeOffEditor
                    resource={formData}
                    idPrefix="resource-time-off"
                    onChange={(value) => handleScheduleUpdate("monthlySchedule", value)}
                  />
                </div>
              </div>

              <div className={modalFooterClass}>
                <button
                  type="button"
                  className={btnGhostClass}
                  onClick={handleCloseModal}
                  disabled={saving}
                >
                  {t("Dashboard.Resources.form.cancel")}
                </button>
                <button
                  type="submit"
                  className={btnPrimaryClass}
                  disabled={saving}
                >
                  {saving
                    ? t("Dashboard.Resources.form.saving")
                    : editingResource
                      ? t("Dashboard.Resources.form.updateResource")
                      : t("Dashboard.Resources.form.addResourceSubmit")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Resources;
